import React, { useEffect, useMemo, useState } from 'react';
import { AdminUserDto, BugReportDto, adminApi, isUnauthorized } from '../../services/adminApi';
import { getAdminTheme } from './adminTheme';

interface AdminBugReportsScreenProps {
  isDarkMode: boolean;
  token: string;
  onUnauthorized: () => void;
}

const AdminBugReportsScreen: React.FC<AdminBugReportsScreenProps> = ({ isDarkMode, token, onUnauthorized }) => {
  const theme = useMemo(() => getAdminTheme(isDarkMode), [isDarkMode]);

  const colors = useMemo(
    () => ({
      card: theme.cardBg,
      border: theme.border,
      text: theme.text,
      subText: theme.subText,
      hover: theme.hoverBg,
      inputBg: theme.inputBg,
      primary: theme.primary,
      danger: theme.danger,
      success: theme.success,
      radius: theme.radius,
    }),
    [theme]
  );

  const [items, setItems] = useState<BugReportDto[] | null>(null);
  const [users, setUsers] = useState<AdminUserDto[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<BugReportDto | null>(null);

  const [filters, setFilters] = useState({
    username: '',
    category: '',
    title: '',
    status: 'all' as 'all' | 'open' | 'resolved',
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: 34,
    borderRadius: colors.radius,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.inputBg,
    padding: '0 10px',
    color: colors.text,
    outline: 'none',
    boxSizing: 'border-box',
    fontSize: 13,
    fontWeight: 700,
  };

  const buttonStyle = (variant: 'primary' | 'neutral' | 'danger' | 'success', disabled?: boolean): React.CSSProperties => {
    const base: React.CSSProperties = {
      height: 34,
      padding: '0 12px',
      borderRadius: colors.radius,
      border: `1px solid ${colors.border}`,
      backgroundColor: colors.card,
      color: colors.text,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 13,
      fontWeight: 800,
      opacity: disabled ? 0.6 : 1,
      whiteSpace: 'nowrap',
    };

    if (variant === 'primary') return { ...base, border: 'none', backgroundColor: colors.primary, color: '#fff' };
    if (variant === 'danger') return { ...base, border: 'none', backgroundColor: colors.danger, color: '#fff' };
    if (variant === 'success') return { ...base, border: 'none', backgroundColor: colors.success, color: '#fff' };
    return base;
  };

  const stripHtml = (html: string): string =>
    String(html || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const refresh = async (opts?: { clearSelection?: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const list = await adminApi.listAdminBugReports(token, { take: 1000 });
      setItems(list);
      if (opts?.clearSelection) setSelectedIds(new Set());
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed loading bug reports');
      setItems(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh({ clearSelection: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const list = await adminApi.listUsers(token);
        if (cancelled) return;
        setUsers(list);
      } catch (e) {
        if (isUnauthorized(e)) {
          onUnauthorized();
          return;
        }
        if (cancelled) return;
        setUsers(null);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, onUnauthorized]);

  const userGroupByUsername = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users ?? []) {
      map.set((u.username ?? '').trim().toLowerCase(), (u.userGroup ?? '').trim());
    }
    return map;
  }, [users]);

  const filteredItems = useMemo(() => {
    const list = items ?? [];
    const usernameQ = filters.username.trim().toLowerCase();
    const categoryQ = filters.category.trim().toLowerCase();
    const titleQ = filters.title.trim().toLowerCase();

    return list.filter((x) => {
      const username = (x.username ?? '').trim().toLowerCase();
      if (usernameQ && !username.includes(usernameQ)) return false;

      if (categoryQ) {
        const g = (userGroupByUsername.get(username) ?? '').toLowerCase();
        if (!g.includes(categoryQ)) return false;
      }

      if (titleQ) {
        const t = (x.title ?? '').trim().toLowerCase();
        if (!t.includes(titleQ)) return false;
      }

      if (filters.status === 'open' && x.isResolved) return false;
      if (filters.status === 'resolved' && !x.isResolved) return false;

      return true;
    });
  }, [filters.category, filters.status, filters.title, filters.username, items, userGroupByUsername]);

  const usernameOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => (u.username ?? '').toString().trim())
      .filter((u) => u.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const categoryOptions = useMemo(() => {
    const list = (users ?? [])
      .map((u) => (u.userGroup ?? '').toString().trim())
      .filter((c) => c.length > 0);
    return Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const cellStyle: React.CSSProperties = {
    padding: '10px 12px',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: 13,
    color: colors.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const headerCellStyle: React.CSSProperties = {
    ...cellStyle,
    position: 'sticky',
    top: 0,
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb',
    zIndex: 1,
    fontSize: 12,
    fontWeight: 900,
    color: colors.subText,
    textAlign: 'left',
  };

  const setResolvedForSelected = async (isResolved: boolean) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      await adminApi.setAdminBugReportsResolved(token, ids, isResolved);
      await refresh({ clearSelection: false });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed updating reports');
    } finally {
      setLoading(false);
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const ok = window.confirm(`Delete ${ids.length} selected bug report(s)?`);
    if (!ok) return;

    setLoading(true);
    setError(null);
    try {
      await adminApi.deleteAdminBugReports(token, ids);
      await refresh({ clearSelection: true });
    } catch (e) {
      if (isUnauthorized(e)) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed deleting reports');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: colors.radius,
        padding: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: colors.text, marginBottom: 2 }}>Bug Reports</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.subText }}>View and manage reports sent from auth menu</div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" style={buttonStyle('neutral')} onClick={() => void refresh({ clearSelection: true })} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            type="button"
            style={buttonStyle('success', loading || selectedIds.size === 0)}
            disabled={loading || selectedIds.size === 0}
            onClick={() => void setResolvedForSelected(true)}
          >
            Mark Resolved
          </button>
          <button
            type="button"
            style={buttonStyle('primary', loading || selectedIds.size === 0)}
            disabled={loading || selectedIds.size === 0}
            onClick={() => void setResolvedForSelected(false)}
          >
            Re-open
          </button>
          <button
            type="button"
            style={buttonStyle('danger', loading || selectedIds.size === 0)}
            disabled={loading || selectedIds.size === 0}
            onClick={() => void deleteSelected()}
          >
            Delete ({selectedIds.size})
          </button>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 10, color: colors.danger, fontSize: 13, fontWeight: 800 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Username</div>
          <input
            value={filters.username}
            onChange={(e) => setFilters((p) => ({ ...p, username: e.target.value }))}
            style={inputStyle}
            placeholder="Select or type..."
            list="admin-bug-reports-username-options"
          />
          <datalist id="admin-bug-reports-username-options">
            {usernameOptions.map((u) => (
              <option key={u} value={u} />
            ))}
          </datalist>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Category</div>
          <input
            value={filters.category}
            onChange={(e) => setFilters((p) => ({ ...p, category: e.target.value }))}
            style={inputStyle}
            placeholder="Select or type..."
            list="admin-bug-reports-category-options"
          />
          <datalist id="admin-bug-reports-category-options">
            {categoryOptions.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Title</div>
          <input
            value={filters.title}
            onChange={(e) => setFilters((p) => ({ ...p, title: e.target.value }))}
            style={inputStyle}
            placeholder="Filter by title"
          />
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 900, color: colors.subText, marginBottom: 6 }}>Status</div>
          <select
            value={filters.status}
            onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value as 'all' | 'open' | 'resolved' }))}
            style={inputStyle}
          >
            <option value="all">All</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>

      <div style={{ overflowX: 'auto', border: `1px solid ${colors.border}`, borderRadius: colors.radius }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 48 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 220 }} />
            <col style={{ width: 200 }} />
            <col style={{ width: 200 }} />
            <col style={{ width: 180 }} />
            <col style={{ width: 120 }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ ...headerCellStyle, textAlign: 'center' }}>
                <input
                  type="checkbox"
                  aria-label="Select all rows"
                  checked={filteredItems.length > 0 && filteredItems.every((x) => selectedIds.has(x.id))}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (checked) filteredItems.forEach((x) => next.add(x.id));
                      else filteredItems.forEach((x) => next.delete(x.id));
                      return next;
                    });
                  }}
                  style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                />
              </th>
              <th style={headerCellStyle}>ID</th>
              <th style={headerCellStyle}>Status</th>
              <th style={headerCellStyle}>Username</th>
              <th style={headerCellStyle}>Title</th>
              <th style={headerCellStyle}>Created</th>
              <th style={headerCellStyle}>Updated</th>
              <th style={headerCellStyle}>Page URL</th>
              <th style={headerCellStyle}>Preview</th>
            </tr>
          </thead>
          <tbody>
            {(filteredItems ?? []).map((x, idx) => {
              const bg = idx % 2 === 0 ? 'transparent' : colors.hover;
              const isSelected = selectedIds.has(x.id);
              return (
                <tr
                  key={x.id}
                  style={{ backgroundColor: isSelected ? (isDarkMode ? colors.hover : '#EEF2FF') : bg, cursor: 'pointer' }}
                  onClick={() => {
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(x.id)) next.delete(x.id);
                      else next.add(x.id);
                      return next;
                    });
                  }}
                >
                  <td style={{ ...cellStyle, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          if (checked) next.add(x.id);
                          else next.delete(x.id);
                          return next;
                        });
                      }}
                      style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                    />
                  </td>
                  <td style={cellStyle}>{x.id}</td>
                  <td style={{ ...cellStyle, color: x.isResolved ? colors.success : colors.danger, fontWeight: 800 }}>
                    {x.isResolved ? 'Resolved' : 'Open'}
                  </td>
                  <td style={cellStyle}>{x.username}</td>
                  <td style={cellStyle} title={x.title}>{x.title}</td>
                  <td style={cellStyle}>{x.createdAtUtc}</td>
                  <td style={cellStyle}>{x.updatedAtUtc}</td>
                  <td style={cellStyle} title={x.pageUrl ?? ''}>{x.pageUrl ?? '—'}</td>
                  <td style={cellStyle}>
                    <button
                      type="button"
                      style={buttonStyle('neutral')}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreview(x);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}

            {filteredItems.length === 0 && (
              <tr>
                <td colSpan={9} style={{ ...cellStyle, color: colors.subText, textAlign: 'center', padding: 18 }}>
                  {loading ? 'Loading…' : 'No items'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {preview && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 9999,
          }}
          onMouseDown={() => setPreview(null)}
        >
          <div
            style={{
              width: 'min(900px, 100%)',
              maxHeight: '80vh',
              overflow: 'auto',
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: colors.radius,
              padding: 14,
              color: colors.text,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900 }}>{preview.title}</div>
                <div style={{ fontSize: 12, color: colors.subText, marginTop: 2 }}>
                  {preview.username} | {preview.category} | {preview.createdAtUtc}
                </div>
              </div>
              <button type="button" style={buttonStyle('neutral')} onClick={() => setPreview(null)}>
                Close
              </button>
            </div>

            <div style={{ marginBottom: 10, fontSize: 12, color: colors.subText }}>
              {preview.pageUrl ? `Page: ${preview.pageUrl}` : 'Page: —'}
            </div>

            <div style={{ border: `1px solid ${colors.border}`, borderRadius: colors.radius, padding: 12 }}>
              <div dangerouslySetInnerHTML={{ __html: preview.html }} />
            </div>

            <div style={{ marginTop: 12, fontSize: 12, color: colors.subText }}>
              Plain text preview: {stripHtml(preview.html).slice(0, 280) || '—'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBugReportsScreen;
